import {
  ChartColumn,
  Folder,
  Mail,
  MessagesSquare,
  Plug,
  type LucideIcon
} from "lucide-react";
import type { FC } from "react";

import { ListCard } from "@/components/ui/list-card";
import { SectionHeader } from "@/components/ui/page-header";

import type { Connector } from "../types";
import { ConnectorCard } from "./ConnectorCard";

type Props = {
  category: string;
  connectors: Connector[];
  pendingSlug: string | null;
  disconnectingSlug: string | null;
  onConnect: (slug: string) => void;
  onDisconnect: (slug: string) => void;
};

const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to"
]);

/** "Email & calendar" → "Email & Calendar"; the catalog sends sentence case. */
export const categoryTitle = (category: string): string =>
  category
    .split(" ")
    .map((w, i) =>
      i > 0 && SMALL_WORDS.has(w.toLowerCase())
        ? w.toLowerCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(" ");

const CATEGORY_ICONS: Array<[RegExp, LucideIcon]> = [
  [/mail|calendar/i, Mail],
  [/file|storage|document/i, Folder],
  [/communicat|chat|meeting|messag/i, MessagesSquare],
  [/productiv|financ|analytic|account/i, ChartColumn]
];

/** The category's leading icon; a plug for categories the catalog adds later. */
export const categoryIcon = (category: string): LucideIcon =>
  CATEGORY_ICONS.find(([re]) => re.test(category))?.[1] ?? Plug;

/** One category: a section label with "n of m connected", then a row per app. */
export const ConnectorGroup: FC<Props> = ({
  category,
  connectors,
  pendingSlug,
  disconnectingSlug,
  onConnect,
  onDisconnect
}) => {
  const connected = connectors.filter((c) => c.status === "connected").length;
  const Icon = categoryIcon(category);
  return (
    <section className="flex flex-col gap-3" data-testid="connector-group">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <Icon className="size-3.5 shrink-0 text-fg-3" aria-hidden />
            {categoryTitle(category)}
          </span>
        }
        actions={
          <span
            className="text-sm text-fg-3 tabular-nums"
            data-testid="connector-group-count"
          >
            {connected} of {connectors.length} connected
          </span>
        }
        data-testid="connector-group-header"
      />
      <ListCard>
        {connectors.map((connector) => (
          <ConnectorCard
            key={connector.slug}
            connector={connector}
            connecting={pendingSlug === connector.slug}
            disconnecting={disconnectingSlug === connector.slug}
            onConnect={onConnect}
            onDisconnect={onDisconnect}
          />
        ))}
      </ListCard>
    </section>
  );
};

/** Groups in first-seen order, which is the catalog's order. */
export const groupByCategory = (
  connectors: Connector[]
): Array<{ category: string; connectors: Connector[] }> => {
  const groups = new Map<string, Connector[]>();
  connectors.forEach((c) => {
    const list = groups.get(c.category) ?? [];
    list.push(c);
    groups.set(c.category, list);
  });
  return Array.from(groups, ([category, list]) => ({
    category,
    connectors: list
  }));
};
